/*
  CIZARO — Quick add (logic half of snippets/cizaro-quick-add.liquid)

  WHY THIS EXISTS
  Horizon's own quick-add fetches the PRODUCT PAGE and lifts out
  [data-product-grid-content], then pulls `variant-picker`,
  `product-form-component` and `product-price` out of it. This store's PDP is
  sections/sections-cizz-alo-product.liquid, which implements its own variant
  picker and buy buttons (.czalo-sizes / .czalo-sizebtn). None of those Horizon
  elements exist, so updateQuickAddModal() never runs — but #openQuickAddModal()
  is called unconditionally on the next line, and the dialog opens empty.
  This talks to Shopify's product JSON endpoint instead, so it depends on
  neither the 99KB header nor the 77KB collection section.

  ⚠ INTERCEPTION IS CAPTURE-PHASE ⚠
  Horizon binds its handler on the <quick-add-component> element. A bubble-phase
  listener would fire AFTER it and the empty dialog would already be opening.
  This listens on document with capture:true and calls stopImmediatePropagation.
  Do not change to bubble.

  ⚠ NO CART-DRAWER DEPENDENCY ⚠
  Deliberately does not try to open #cz-cart-drawer after adding. The opener's
  selector is inside the 99KB header section and unverified, and guessing wrong
  means a silent no-op after a successful add. The sheet shows its own
  confirmation with a link to the bag instead.

  ──────────────────────────────────────────────────────────
  THE PAGE'S SIZE IS THE SOURCE OF TRUTH (17 Oct 2026 — audit CZ-002)
  ──────────────────────────────────────────────────────────
  The audit reproduced this: choose size 42 on a product page, press Add to
  Cart, and the sheet opens on the SAVED size 36 — which is then what lands in
  the bag. Wrong-size orders, exchanges, and in a COD market refusals at the
  door.

  The cause is a collision. This sheet used to open with `sel = {}` and nothing
  marked selected; cz-my-sizes.js watches #czqa and, seeing no chip carrying
  .is-sel, clicks the saved-size chip. Its guard is literally "has anything
  been selected yet" — so an empty sheet always loses to the saved size.

  Fixed by seeding, in seedFromPage(), inside load() rather than at the call
  site: whatever opens the sheet for the product the page is showing, the
  page's own selection wins.

    1. If the page has a COMPLETE, purchasable selection, no sheet is shown at
       all — that variant is added directly. This is the audit's own
       recommendation: when a size is chosen, do not ask again.
    2. Otherwise the sheet opens pre-seeded with whatever the page had, and
       render() stamps .is-sel on those chips. That satisfies cz-my-sizes'
       guard, so it annotates "your size" as before but no longer overrides
       the choice. No change to the minified cz-my-sizes.js was needed.
    3. With no page selection at all — a card on a collection or the home page
       — behaviour is unchanged and the saved size is still offered.
*/
(function () {
  if (window.__czQA) return;

  var cfgEl = document.getElementById('czqa-config');
  var CFG = {};
  try { CFG = JSON.parse(cfgEl.textContent); } catch (e) {}

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var T = CFG.t || {};

  var scrim = document.getElementById('czqa-scrim');
  var sheet = document.getElementById('czqa');
  if (!scrim || !sheet) return;

  var sel = {};
  var product = null;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Shopify's product JSON gives prices in cents. Formatting is deliberately
     simple — the money format string is not exposed to JS reliably, so this
     mirrors what the cards already show rather than inventing a new format. */
  function money(cents) {
    var n = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return CFG.ar ? n + ' LE' : 'LE ' + n;
  }

  function open() {
    scrim.hidden = false;
    sheet.hidden = false;
    sheet.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(function () {
      scrim.classList.add('is-open');
      sheet.classList.add('is-open');
    });
    document.documentElement.style.overflow = 'hidden';
  }

  function close() {
    scrim.classList.remove('is-open');
    sheet.classList.remove('is-open');
    sheet.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    setTimeout(function () {
      scrim.hidden = true;
      sheet.hidden = true;
      sheet.innerHTML = '';
    }, 400);
  }

  function handleFromPath(p) {
    var m = String(p || '').split('?')[0].match(/\/products\/([^\/#]+)/);
    if (!m) return null;
    try { return decodeURIComponent(m[1]).toLowerCase(); }
    catch (e) { return m[1].toLowerCase(); }
  }

  /* The PDP's own variant state, option index -> chosen value, or null where
     nothing is chosen yet. Selectors match sections/sections-cizz-alo-product
     and are the same ones cz-my-sizes.js reads. */
  function pageSelection() {
    var root = document.querySelector('.czalo[data-section-id]');
    if (!root) return null;
    var groups = root.querySelectorAll('[data-czalo-option]');
    if (!groups.length) return null;
    var out = [];
    groups.forEach(function (g) {
      var i = parseInt(g.getAttribute('data-option-index'), 10);
      if (isNaN(i)) return;
      var on = g.querySelector('.is-selected[data-czalo-value]')
        || g.querySelector('[data-czalo-value].is-selected');
      out[i] = on ? on.getAttribute('data-czalo-value') : null;
    });
    return out.length ? out : null;
  }

  /* A variant matches when every one of its options equals the current pick. */
  function match() {
    if (!product) return null;
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      var ok = true;
      for (var o = 0; o < product.options.length; o++) {
        if (sel[o] !== v.options[o]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  /* Is any variant with this option value in stock? Drives the strike-through. */
  function valueAvailable(optIndex, value) {
    return product.variants.some(function (v) {
      return v.options[optIndex] === value && v.available;
    });
  }

  function syncFoot() {
    var btn = sheet.querySelector('.czqa__atc');
    if (!btn) return;
    var v = match();
    var need = product.options.length;
    var chosen = Object.keys(sel).length;

    if (chosen < need) { btn.disabled = true; btn.textContent = T.pick; return; }
    if (!v || !v.available) { btn.disabled = true; btn.textContent = T.out; return; }
    btn.disabled = false;
    btn.textContent = T.atc;
  }

  function render(href) {
    var img = product.featured_image ? product.featured_image : '';
    var v0 = match() || product.variants[0];
    var onSale = v0.compare_at_price && v0.compare_at_price > v0.price;

    var html =
      '<div class="czqa__head">' +
        (img ? '<img class="czqa__img" src="' + esc(img) + '" alt="">' : '') +
        '<div class="czqa__meta">' +
          '<a class="czqa__title" href="' + esc(href) + '">' + esc(product.title) + '</a>' +
          '<span class="czqa__price' + (onSale ? ' is-sale' : '') + '">' + money(v0.price) +
            (onSale ? '<s>' + money(v0.compare_at_price) + '</s>' : '') +
          '</span>' +
        '</div>' +
        '<button class="czqa__x" type="button" aria-label="' + esc(T.close) + '">&times;</button>' +
      '</div><div class="czqa__body">';

    product.options.forEach(function (opt, oi) {
      var name = typeof opt === 'string' ? opt : opt.name;
      html += '<p class="czqa__optname">' + esc(name) + '</p><div class="czqa__chips">';
      var list = opt.values || (product.variants.map(function (v) { return v.options[oi]; })
        .filter(function (x, i, a) { return a.indexOf(x) === i; }));
      list.forEach(function (val) {
        var out = !valueAvailable(oi, val);
        /* .is-sel is stamped here, not left to a later click. cz-my-sizes.js
           only overrides a sheet where NOTHING is selected — see CZ-002. */
        var on = sel[oi] === val;
        html += '<button type="button" class="czqa__chip' + (out ? ' is-out' : '') +
          (on ? ' is-sel' : '') +
          '" data-oi="' + oi + '" data-val="' + esc(val) + '"' + (out ? ' disabled' : '') + '>' +
          esc(val) + '</button>';
      });
      html += '</div>';
    });

    html += '</div><div class="czqa__foot">' +
      '<button type="button" class="czqa__atc" disabled>' + esc(T.pick) + '</button>' +
      '<p class="czqa__note">' + esc(T.cod) + '</p></div>';

    sheet.innerHTML = html;

    /* Single-option products with one value: preselect so it is one tap. */
    if (product.options.length === 1 && sel[0] == null) {
      var only = sheet.querySelectorAll('.czqa__chip:not(.is-out)');
      if (only.length === 1) only[0].click();
    }
    syncFoot();
  }

  function done() {
    sheet.innerHTML =
      '<div class="czqa__head"><div class="czqa__meta"></div>' +
      '<button class="czqa__x" type="button" aria-label="' + esc(T.close) + '">&times;</button></div>' +
      '<div class="czqa__done"><p>' + esc(T.done) + '</p><div class="czqa__links">' +
      '<a href="' + esc(ROOT.replace(/\/$/, '')) + '/cart">' + esc(T.bag) + '</a>' +
      '<button type="button" class="czqa__more">' + esc(T.more) + '</button>' +
      '</div></div>';
  }

  /* CZ-002. Only for the product the page is actually showing — a card for a
     DIFFERENT product must not inherit this page's size. */
  function seedFromPage(handle) {
    sel = {};
    if (handleFromPath(location.pathname) !== handle) return;
    var picked = pageSelection();
    if (!picked) return;
    for (var i = 0; i < product.options.length; i++) {
      if (picked[i] != null) sel[i] = picked[i];
    }
  }

  async function add(id) {
    var res = await fetch(ROOT.replace(/\/$/, '') + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
    });
    if (!res.ok) throw new Error('cart add ' + res.status);
    document.dispatchEvent(new CustomEvent('cart:update'));
    return res.json();
  }

  async function load(handle, href) {
    var url = ROOT.replace(/\/$/, '') + '/products/' + handle + '.js';
    var res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('product fetch ' + res.status);
    product = await res.json();
    window.__czQA.last = product;

    seedFromPage(String(handle).toLowerCase());

    /* Complete and purchasable already? Do not ask the shopper twice. */
    var v = match();
    if (v && v.available && Object.keys(sel).length === product.options.length) {
      await add(v.id);
      done();
      open();
      return;
    }

    render(href);
    open();
  }

  /* ── Interception ── capture phase, see the ⚠ note above. ── */
  document.addEventListener('click', function (e) {
    var qa = e.target.closest && e.target.closest('quick-add-component');
    if (!qa) return;

    var card = qa.closest('product-card') || qa.closest('product-hotspot-component') || qa.parentElement;
    var link = card && card.querySelector('a[href*="/products/"]');
    if (!link) return; /* no handle to work with — let Horizon try */

    e.preventDefault();
    e.stopImmediatePropagation();

    var handle = link.getAttribute('href').split('/products/')[1].split('?')[0].split('#')[0];
    load(handle, link.href).catch(function (err) {
      console.warn('[czQA]', err);
    });
  }, true);

  /* Sheet interactions, delegated so re-renders need no re-binding. */
  sheet.addEventListener('click', function (e) {
    var t = e.target;

    if (t.closest('.czqa__x') || t.closest('.czqa__more')) { close(); return; }

    var chip = t.closest('.czqa__chip');
    if (chip && !chip.disabled) {
      var oi = chip.dataset.oi;
      sel[oi] = chip.dataset.val;
      sheet.querySelectorAll('.czqa__chip[data-oi="' + oi + '"]').forEach(function (c) {
        c.classList.toggle('is-sel', c === chip);
      });
      syncFoot();
      return;
    }

    var atc = t.closest('.czqa__atc');
    if (atc && !atc.disabled) {
      var v = match();
      if (!v) return;
      atc.disabled = true;
      atc.textContent = T.adding;
      add(v.id).then(function () {
        done();
      }).catch(function (err) {
        console.warn('[czQA]', err);
        atc.disabled = false;
        atc.textContent = T.atc;
        var note = sheet.querySelector('.czqa__note');
        if (note) { note.textContent = T.err; note.style.color = '#c42b2b'; }
      });
    }
  });

  scrim.addEventListener('click', close);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !sheet.hidden) close();
  });

  window.__czQA = { open: load, close: close, last: null };
})();
